import { createTheme } from '@material-ui/core/styles';
import Kobenhavn from 'static/media/Kobenhavn.ttf';

const kobenhavn = {
  fontFamily: 'Kobenhavn',
  fontStyle: 'normal',
  fontWeight: 'normal',
  src: `
    local('Kobenhavn'),
    url(${Kobenhavn}) format('embedded-opentype'),
    url(${Kobenhavn}) format('woff2'),
    url(${Kobenhavn}) format('woff'),
    url(${Kobenhavn}) format('truetype'),
    url(${Kobenhavn}) format('svg')
  `,
  unicodeRange:
    'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF',
};

export const primaryColor = (alpha = '1') => {
  return `rgba(40, 71, 92,${alpha})`; // cadet
};
export const secondaryColor = (alpha = '1') => {
  return `rgba(91, 110, 125,${alpha})`;
};
export const buttonPrimaryColor = () => {
  return 'rgba(0,166,75,1)'; // green
};
const buttonHoverColor = () => {
  return 'rgba(70, 181, 113, 1)';
};

const buttonActiveColor = () => {
  return 'rgba(0, 100, 58, 1)';
};

export const buttonInactiveColor = () => {
  return 'rgba(180, 184, 194, 1)';
};

export const warningColor = 'rgb(237,106,50)';
export const errorColor = 'rgb(244,67,54)';

const defaultTheme = createTheme();

const theme = createTheme({
  palette: {
    type: 'light',
    primary: {
      main: primaryColor(),
      light: primaryColor('0.1'),
      dark: primaryColor('0.8'),
    },
    secondary: {
      main: secondaryColor(),
      light: secondaryColor('0.1'),
      dark: secondaryColor('0.9'),
    },
    error: {
      main: warningColor,
    },
    background: {
      default: secondaryColor('0.1'),
    },
  },
  spacing: (factor) => `${0.5 * factor}rem`,
  typography: {
    fontFamily: ['Calibri', 'Arial', 'sans-serif'].join(','),
    fontSize: 14,
    body2: {
      fontSize: '0.9rem',
    },
    body1: {
      fontSize: '0.9rem',
    },

    subtitle1: {
      fontSize: '1rem',
      fontFamily: [kobenhavn.fontFamily, 'Calibri', 'Arial', 'sans-serif'].join(
        ',',
      ),
    },
    h1: {
      fontSize: '2rem',
      fontFamily: [kobenhavn.fontFamily, 'Calibri', 'Arial', 'sans-serif'].join(
        ',',
      ),
    },
    h2: {
      fontSize: '1.8rem',
      fontFamily: [kobenhavn.fontFamily, 'Calibri', 'Arial', 'sans-serif'].join(
        ',',
      ),
    },
    h3: {
      fontSize: '1.6rem',
      fontFamily: [kobenhavn.fontFamily, 'Calibri', 'Arial', 'sans-serif'].join(
        ',',
      ),
    },
    h4: {
      fontSize: '1.4rem',
      fontFamily: [kobenhavn.fontFamily, 'Calibri', 'Arial', 'sans-serif'].join(
        ',',
      ),
    },
    h5: {
      fontSize: '1.2rem',
      fontFamily: [kobenhavn.fontFamily, 'Calibri', 'Arial', 'sans-serif'].join(
        ',',
      ),
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': [kobenhavn],
        body: {
          backgroundColor: '#F2F3F5',
        },
      },
    },
    MuiToolbar: {
      gutters: {
        [defaultTheme.breakpoints.up('sm')]: {
          paddingLeft: '1rem',
          paddingRight: '1rem',
        },
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: '#fff',
        color: primaryColor(),
      },
    },
    MuiDialog: {
      paperWidthMd: {
        maxWidth: '50rem',
      },
      paper: {
        padding: '2rem',
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '0 0 1.5rem 0',
      },
    },
    MuiDialogContent: {
      root: {
        paddingLeft: '0',
        paddingRight: '0',
        overflow: 'hidden',
        height: '9.5rem',
        flex: 'auto',
        whiteSpace: 'pre-line',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '2rem 0 0 0',
      },
    },
    MuiTableContainer: {
      root: {
        background: '#ffffff',
      },
    },
    MuiButton: {
      root: {
        margin: '0.5rem',
        minWidth: '8rem',
        '&$disabled': {
          color: buttonInactiveColor(),
        },
      },
      contained: {
        margin: '0.5rem',
        boxShadow: 'none',
        '&$disabled': {
          backgroundColor: buttonInactiveColor(),
          color: '#fff',
          boxShadow: 'none',
        },
        '&:hover': {
          backgroundColor: buttonHoverColor(),
          boxShadow: 'none',
        },
      },
      containedPrimary: {
        backgroundColor: buttonPrimaryColor(),
        '&:hover': {
          backgroundColor: buttonHoverColor(),
        },
      },
      outlinedSecondary: {
        '&:hover': {},
        '&:active': {
          backgroundColor: buttonActiveColor(),
          color: '#fff',
          borderColor: buttonHoverColor(),
        },
        '&$disabled': {
          backgroundColor: '#fff',
          color: buttonInactiveColor(),
          borderColor: buttonInactiveColor(),
        },
      },
      textPrimary: {
        color: primaryColor(),
      },
      textSecondary: {
        color: secondaryColor(),
      },
    },
    MuiTextField: {
      root: {
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            borderColor: buttonPrimaryColor(),
          },
        },
      },
    },
    MuiFormControl: {
      root: {
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            borderColor: buttonPrimaryColor(),
          },
        },
      },
    },
    MuiStepLabel: {
      active: {
        '&.MuiStepLabel-active': {
          fontWeight: 'bold',
          color: primaryColor(),
        },
        '&.MuiStepLabel-completed': {
          color: primaryColor(),
        },
      },
    },
    MuiInputLabel: {
      root: {
        '&$focused': {
          color: buttonPrimaryColor(),
        },
      },
      outlined: {
        '&$shrink': {
          color: buttonPrimaryColor(),
        },
      },
    },
    MuiLink: {
      root: {
        fontWeight: 'bold',
      },
    },
    MuiSnackbarContent: {
      root: {
        backgroundColor: primaryColor(),
        color: '#fff',
      },
    },
  },
});

const appBarTheme = createTheme({
  ...theme,
  overrides: {
    ...theme.overrides,
    MuiOutlinedInput: {
      root: {
        color: '#fff',
        '&$focused $notchedOutline': {
          borderColor: '#fff',
          borderWidth: 1,
        },
        '&:hover $notchedOutline': {
          borderColor: '#fff',
        },
      },
      notchedOutline: {
        borderColor: '#fff',
      },
    },
    MuiSelect: {
      root: {
        paddingTop: '0.7rem',
        paddingBottom: '0.7rem',
      },
      iconOutlined: {
        color: '#fff',
      },
    },
  },
});

const kobenhavnFont = createTheme({
  ...theme,
  typography: {
    fontFamily: [kobenhavn.fontFamily, 'Calibri', 'Arial', 'sans-serif'].join(
      ',',
    ),
  },
  overrides: {
    MuiListItem: {
      root: {
        paddingTop: '0.2rem',
        paddingBottom: '0.2rem',
        marginBottom: '0.5rem',
        '&$selected': {
          backgroundColor: primaryColor(),
          '&:hover': {
            backgroundColor: 'rgb(113,130,143)',
          },
        },

        '&:hover': {
          backgroundColor: 'rgb(113,130,143)',
        },
      },
    },
  },
});

export { theme, kobenhavnFont, appBarTheme };
